import React, { createContext, useCallback, useContext, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useUser } from './useUser'
import { supabase } from '../agents/db'
import {
    type CmsPermissions, FULL_CMS_ACCESS, NO_CMS_ACCESS, hasAnyCmsAccess, parseCmsPermissions,
} from '@/pages/dev/smart-cms/shared/permissions'
import { parseAssistantPermissions } from '@/lib/assistant/permissions'
import type { AssistantPermissions } from '@/lib/assistant/types'

type PermissionAction = 'create' | 'read' | 'update' | 'delete' | 'share'

interface Permission {
    resource: string
    create: boolean
    read: boolean
    update: boolean
    delete: boolean
    share: boolean
}

interface PermissionsValue {
    permissions: Permission[]
    hasPermission: (resource: string, action: PermissionAction) => boolean
    canCreate: (resource: string) => boolean
    canRead: (resource: string) => boolean
    canUpdate: (resource: string) => boolean
    canDelete: (resource: string) => boolean
    canShare: (resource: string) => boolean
    cms: CmsPermissions
    assistant: AssistantPermissions
    loading: boolean
}

const PermissionsContext = createContext<PermissionsValue | null>(null)

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
    const { data: user, isLoading: userLoading } = useUser()

    const { data: roleData, isLoading: permissionsLoading } = useQuery<{ permissions: Permission[], cms: CmsPermissions | null, assistant: AssistantPermissions | null }>({
        queryKey: ['permissions', user?.role],
        queryFn: async () => {
            if (!user?.role) {
                return { permissions: [], cms: null, assistant: null }
            }
            // select * so this still works before the cms_permissions column exists
            const { data: role, error } = await supabase.from("roles").select("*").eq("id", user.role).single()
            if (error) {
                return { permissions: [], cms: null, assistant: null }
            }
            return {
                permissions: ((role?.permissions || []) as unknown) as Permission[],
                cms: parseCmsPermissions(role?.cms_permissions),
                assistant: parseAssistantPermissions((role as any)?.assistant_permissions),
            }
        },
        enabled: !!user?.role && !userLoading,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    })

    const permissions = useMemo(() => roleData?.permissions ?? [], [roleData])

    const checkResource = useCallback((resource: string, action: PermissionAction): boolean => {
        if (userLoading || permissionsLoading) {
            return false
        }
        if (!user?.role) {
            return false
        }
        const resourcePermission = permissions.find(p => p.resource === resource)
        return resourcePermission ? resourcePermission[action] : false
    }, [permissions, user?.role, userLoading, permissionsLoading])

    // Roles without cms_permissions keep the old behaviour: dashboard read meant full CMS access
    const cms = useMemo<CmsPermissions>(() => {
        if (roleData?.cms) return roleData.cms
        return checkResource("dashboard", "read") ? FULL_CMS_ACCESS : NO_CMS_ACCESS
    }, [roleData, checkResource])

    // Roles without assistant_permissions use their CMS agent settings
    const assistant = useMemo<AssistantPermissions>(() => roleData?.assistant ?? cms.agent, [roleData, cms])

    const hasPermission = useCallback((resource: string, action: PermissionAction): boolean => {
        // "cms" is virtual: readable when any CMS section is visible
        if (resource === "cms") return action === "read" && hasAnyCmsAccess(cms)
        return checkResource(resource, action)
    }, [checkResource, cms])

    const value = useMemo<PermissionsValue>(() => ({
        permissions,
        hasPermission,
        canCreate: (r: string) => hasPermission(r, 'create'),
        canRead: (r: string) => hasPermission(r, 'read'),
        canUpdate: (r: string) => hasPermission(r, 'update'),
        canDelete: (r: string) => hasPermission(r, 'delete'),
        canShare: (r: string) => hasPermission(r, 'share'),
        cms,
        assistant,
        loading: userLoading || permissionsLoading
    }), [permissions, hasPermission, cms, assistant, userLoading, permissionsLoading])

    return React.createElement(PermissionsContext.Provider, { value }, children)
}

export function usePermissions(): PermissionsValue {
    const ctx = useContext(PermissionsContext)
    if (!ctx) {
        return {
            permissions: [],
            hasPermission: () => false,
            canCreate: () => false,
            canRead: () => false,
            canUpdate: () => false,
            canDelete: () => false,
            canShare: () => false,
            cms: NO_CMS_ACCESS,
            assistant: NO_CMS_ACCESS.agent,
            loading: true
        }
    }
    return ctx
}